// Block editing for TTSView: text/lock/duplicate/merge/split/delete/move,
// per-block download, Reset Positions, reorder remap, Auto-Split,
// Restore-Script, take chips (star, delete, first-take star hint), Add
// Block, and the ParamPanel writes (params, per-block voice).
//
// Extracted from TTSView unchanged. Handlers that were plain functions
// there are plain functions here (recreated per render, as before);
// memoised ones keep their exact dependency lists.

import { useCallback, useMemo, useState } from 'react';
import type { Asset, Block as BlockT, TtsParams } from '@shared/types';
import { useSessionStore } from '@/stores/session';
import type { useToast } from '@/components/ToastProvider';
import { buildBlockFilename } from '@/lib/uploadFilenames';
import { assetGlobalIdx } from '@/lib/assetNumber';
import { triggerDownload } from '@/lib/audioExport';
import { voiceTrackIndex } from '@/lib/voiceMapping';
import { joinSentences, splitSentences } from '@/lib/splitSentences';
import { autoPositionFavOnTimeline } from '@/lib/autoPositionFav';
import { newBlockId } from '@/lib/blockId';
import { cloneBlockForDuplicate } from '@/lib/duplicateBlock';
import { resetPositionsPerTrack, resolveTimelineOverlapsPerTrack } from '@/lib/timeline';
import { resolveBlockVoiceId, resolveTakeVoiceId } from '@/lib/voiceMigration';
import { hasSeenStarHint, markStarHintSeen } from '@/lib/starHint';
import { defaultParams, makeEmptyBlock } from '../blockFactory';
import type { useTakePlayback } from './useTakePlayback';

interface UseBlockEditingOptions {
  blocks: BlockT[];
  activeBlock: BlockT | undefined;
  activeBlockIdx: number;
  setActiveBlockIdx: (idx: number) => void;
  setNewBlockIdx: (idx: number | null) => void;
  bumpSkip: () => void;
  activeAsset: (Asset & { _isCustom?: boolean }) | undefined;
  activeAssetVoiceId: string;
  currentAssetIdx: number;
  langCode: string;
  shortId: string;
  nextVersion: number;
  toast: ReturnType<typeof useToast>;
  playback: ReturnType<typeof useTakePlayback>;
}

export function useBlockEditing({
  blocks,
  activeBlock,
  activeBlockIdx,
  setActiveBlockIdx,
  setNewBlockIdx,
  bumpSkip,
  activeAsset,
  activeAssetVoiceId,
  currentAssetIdx,
  langCode,
  shortId,
  nextVersion,
  toast,
  playback,
}: UseBlockEditingOptions) {
  const updateActiveBlocks = useSessionStore((s) => s.updateActiveBlocks);
  const removeTakeFromStore = useSessionStore((s) => s.removeTake);
  const setBlockFavIdx = useSessionStore((s) => s.setBlockFavIdx);
  const { playing, stopPlayback, pipelineMode, cancelPipeline } = playback;

  // ── Block mutations ──────────────────────────────────────────────────
  const handleTextChange = (idx: number, text: string) => {
    updateActiveBlocks((prev) => {
      if (!prev[idx]) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], text };
      return next;
    });
  };

  const handleToggleLock = (idx: number) => {
    updateActiveBlocks((prev) => {
      if (!prev[idx]) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], locked: !prev[idx].locked };
      return next;
    });
  };

  const handleDuplicate = (idx: number) => {
    updateActiveBlocks((prev) => {
      const src = prev[idx];
      if (!src) return prev;
      // Klon ohne Stern und ohne Timeline-Position — siehe lib/duplicateBlock.ts.
      const copy = cloneBlockForDuplicate(src);
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
    setActiveBlockIdx(idx + 1);
    setNewBlockIdx(idx + 1);
    bumpSkip();
  };

  const handleMergeUp = (idx: number) => {
    if (idx === 0) return;
    const above = blocks[idx - 1];
    const here = blocks[idx];
    if (!above || !here) return;
    // FB#19: locked block cannot be the merge TARGET either — merging would
    // overwrite its text content, defeating the lock. Vanilla missed this
    // case (only blocked merge-FROM-locked). Locked source already blocked
    // upstream by the disabled button on the locked block itself.
    if (above.locked || here.locked) return;
    // FB#7 + FB#8: merging clears takes from BOTH source AND target — the
    // merged text differs from either original, so neither block's takes
    // match anymore. Confirm if either side has takes.
    const takeCount = above.takes.length + here.takes.length;
    if (takeCount > 0) {
      const ok = window.confirm(
        `This will delete ${takeCount} take${takeCount === 1 ? '' : 's'} from the merged blocks. Continue?`,
      );
      if (!ok) return;
    }
    updateActiveBlocks((prev) => {
      const a = prev[idx - 1];
      const h = prev[idx];
      if (!a || !h) return prev;
      const merged: BlockT = {
        ...a,
        text: `${a.text} ${h.text}`.trim(),
        takes: [],
        favIdx: -1,
        takeCounter: 0,
      };
      return [...prev.slice(0, idx - 1), merged, ...prev.slice(idx + 1)];
    });
    setActiveBlockIdx(idx - 1);
    bumpSkip();
  };

  const handleMergeDown = (idx: number) => {
    const here = blocks[idx];
    const below = blocks[idx + 1];
    if (!here || !below) return;
    // FB#19: locked block cannot be the merge TARGET either. Locked source
    // already blocked by the disabled button on the locked block.
    if (here.locked || below.locked) return;
    // FB#7 + FB#8: clear takes from both sides on merge.
    const takeCount = here.takes.length + below.takes.length;
    if (takeCount > 0) {
      const ok = window.confirm(
        `This will delete ${takeCount} take${takeCount === 1 ? '' : 's'} from the merged blocks. Continue?`,
      );
      if (!ok) return;
    }
    updateActiveBlocks((prev) => {
      const h = prev[idx];
      const b = prev[idx + 1];
      if (!h || !b) return prev;
      const merged: BlockT = {
        ...h,
        text: `${h.text} ${b.text}`.trim(),
        takes: [],
        favIdx: -1,
        takeCounter: 0,
      };
      return [...prev.slice(0, idx), merged, ...prev.slice(idx + 2)];
    });
    bumpSkip();
  };

  const handleSplit = (idx: number) => {
    updateActiveBlocks((prev) => {
      const src = prev[idx];
      if (!src) return prev;
      const sentences = splitSentences(src.text);
      if (sentences.length < 2) return prev;
      const half = Math.ceil(sentences.length / 2);
      const firstText = joinSentences(sentences.slice(0, half));
      const secondText = joinSentences(sentences.slice(half));
      const first: BlockT = { ...src, text: firstText, takes: [], favIdx: -1, takeCounter: 0 };
      // `first` behält die ID des Quellblocks (ein laufender Generate findet
      // ihn weiter), `second` bekommt über makeEmptyBlock eine neue.
      const second: BlockT = {
        ...makeEmptyBlock('', src.voiceId),
        params: src.params,
        text: secondText,
      };
      return [...prev.slice(0, idx), first, second, ...prev.slice(idx + 1)];
    });
    setNewBlockIdx(idx + 1);
    bumpSkip();
  };

  const handleDelete = (idx: number) => {
    // Always-confirm pattern — Delete buttons sit close to other controls
    // and an accidental click would otherwise lose the script + any takes
    // in one go (no undo). Confirmation applies whether the block has
    // takes or not so the gesture feels predictable.
    const block = blocks[idx];
    const takeCount = block?.takes.length ?? 0;
    const msg =
      takeCount > 0
        ? `Delete Block ${idx + 1}? This also discards ${takeCount} take${takeCount === 1 ? '' : 's'}.`
        : `Delete Block ${idx + 1}?`;
    if (!window.confirm(msg)) return;
    updateActiveBlocks((prev) => {
      if (prev.length <= 1) return prev;
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });
    if (activeBlockIdx >= idx && activeBlockIdx > 0) {
      setActiveBlockIdx(activeBlockIdx - 1);
    }
    bumpSkip();
  };

  const handleDownloadBlock = (idx: number) => {
    const block = blocks[idx];
    const favTake = block?.takes[block?.favIdx];
    if (!favTake || !activeAsset) {
      toast.show('Favorite a take first to download', 2500);
      return;
    }
    // Multi-Voice (#173): emit the same `_VoiceN` suffix the upload pipeline
    // uses so per-block downloads stay consistent with the bundled upload
    // payload. Single-voice assets get no suffix (byte-identical pre-#173).
    const assetVoiceIds = activeAsset.voiceIds ?? [];
    let voiceMarker: number | undefined;
    if (assetVoiceIds.length >= 2) {
      const blockVoice = resolveBlockVoiceId(block, assetVoiceIds);
      const takeVoice = resolveTakeVoiceId(favTake, blockVoice, assetVoiceIds);
      const trackIdx = voiceTrackIndex(takeVoice, assetVoiceIds);
      voiceMarker = trackIdx >= 0 ? trackIdx + 1 : undefined;
    }
    const filename = buildBlockFilename({
      langCode,
      shortId,
      assetIdx: assetGlobalIdx(activeAsset, currentAssetIdx),
      assetTitle: activeAsset.title,
      version: nextVersion,
      blockIdx: idx,
      voiceMarker,
    });
    triggerDownload(new Blob([favTake.buffer], { type: 'audio/mpeg' }), filename);
  };

  // Swap two adjacent blocks in array AND swap their chip offsets so the
  // chip moves visually with the block. Other chips keep their offsets
  // (gaps preserved); resolveTimelineOverlaps with the lower-offset block
  // as pin pushes following chips right only if the new chip overflows
  // its slot due to a longer duration.
  const swapAdjacent = (prev: BlockT[], lowerIdx: number): BlockT[] => {
    if (lowerIdx < 0 || lowerIdx >= prev.length - 1) return prev;
    const a = prev[lowerIdx];
    const b = prev[lowerIdx + 1];
    const aHasFav = a.favIdx >= 0 && !!a.takes[a.favIdx];
    const bHasFav = b.favIdx >= 0 && !!b.takes[b.favIdx];
    let newA: BlockT = a;
    let newB: BlockT = b;
    if (aHasFav && bHasFav) {
      // Swap offsets between the two chips so each chip ends up at the
      // other's old slot. Pin the chip now at the smaller offset so the
      // resolver pushes subsequent chips right when needed.
      newA = { ...a, offset: b.offset, pinned: true };
      newB = { ...b, offset: a.offset, pinned: true };
    } else if (aHasFav && !bHasFav) {
      newA = { ...a, pinned: true };
    } else if (!aHasFav && bHasFav) {
      newB = { ...b, pinned: true };
    }
    const next = prev.slice();
    next[lowerIdx] = newB;
    next[lowerIdx + 1] = newA;
    // Pin the moved chip at its new (smaller) offset and let the resolver
    // push neighbours right only when the swap creates an overlap. Per-track
    // (#173): cross-voice chips remain free to overlap.
    return resolveTimelineOverlapsPerTrack(next, activeAsset?.voiceIds ?? [], lowerIdx, 0);
  };

  const handleMoveUp = (idx: number) => {
    if (idx === 0) return;
    updateActiveBlocks((prev) => swapAdjacent(prev, idx - 1));
    if (activeBlockIdx === idx) setActiveBlockIdx(idx - 1);
    else if (activeBlockIdx === idx - 1) setActiveBlockIdx(idx);
    bumpSkip();
  };

  const handleMoveDown = (idx: number) => {
    updateActiveBlocks((prev) => swapAdjacent(prev, idx));
    if (activeBlockIdx === idx) setActiveBlockIdx(idx + 1);
    else if (activeBlockIdx === idx + 1) setActiveBlockIdx(idx);
    bumpSkip();
  };

  // Reset Positions handler passed into ExportBar. Per Voice-Spur wird die
  // Block-Reihenfolge anhand der aktuellen Visual-Offsets sortiert und
  // sequenziell ab t=0 gepackt. Bei Multi-Voice (#173) sind die Spuren
  // unabhängig — Cross-Voice-Chips dürfen denselben Offset haben.
  //
  // Die Block-Array-Reihenfolge bleibt unverändert (kein reorderByOffset
  // mehr), weil ein globales Reorder Cross-Voice-Ordering willkürlich
  // verschränken würde. Damit entfällt auch das frühere activeBlockIdx-Remap.
  const handleResetPositions = useCallback(() => {
    const ok = window.confirm(
      'Reset block positions? Chips snap to sequential order based on their current left-to-right position on the timeline.',
    );
    if (!ok) return;
    const voiceIds = activeAsset?.voiceIds ?? [];
    updateActiveBlocks((prev) => resetPositionsPerTrack(prev, voiceIds));
    bumpSkip();
    toast.show('Block positions reset', 2000);
  }, [activeAsset?.voiceIds, bumpSkip, toast, updateActiveBlocks]);

  // Fired by Timeline after a chip drop reorders the block array (chip
  // positions in left-to-right order are now the new array order). Remap
  // activeBlockIdx so the param panel keeps pointing at the same block.
  const handleBlocksReordered = useCallback(
    (indexMap: Map<number, number>) => {
      const newIdx = indexMap.get(activeBlockIdx);
      if (typeof newIdx === 'number' && newIdx !== activeBlockIdx) {
        setActiveBlockIdx(newIdx);
        bumpSkip();
      }
    },
    [activeBlockIdx, bumpSkip, setActiveBlockIdx],
  );

  // Top-level Auto-Split: joins ALL blocks' text, splits at sentence
  // boundaries, replaces the entire block list with one block per sentence.
  // Mirrors legacy `autoSplit()` in legacy/index.html:4827. Inline label
  // (`autoSplitFeedback`) shows "N sentences detected" / "No sentence
  // boundaries detected" briefly after running.
  const [autoSplitFeedback, setAutoSplitFeedback] = useState<string>('');
  const handleAutoSplitAll = () => {
    if (pipelineMode !== 'idle') cancelPipeline();
    const fullText = blocks.map((b) => b.text).join(' ').trim();
    const sentences = splitSentences(fullText);
    if (sentences.length <= 1) {
      setAutoSplitFeedback('No sentence boundaries detected');
      return;
    }
    if (blocks.some((b) => b.takes.length > 0)) {
      if (!window.confirm('This will delete all existing takes. Continue?')) return;
    }
    stopPlayback();
    const baseParams = blocks[0]?.params ?? defaultParams();
    const baseVoiceId = blocks[0]?.voiceId ?? activeAssetVoiceId;
    const next: BlockT[] = sentences.map((text) => ({
      id: newBlockId(),
      text,
      params: baseParams,
      takes: [],
      favIdx: -1,
      takeCounter: 0,
      locked: false,
      offset: 0,
      pinned: false,
      voiceId: baseVoiceId,
    }));
    updateActiveBlocks(() => next);
    setActiveBlockIdx(0);
    setNewBlockIdx(0);
    bumpSkip();
    setAutoSplitFeedback(`${sentences.length} sentences detected`);
  };

  // Script reference panel (vanilla #assetScript): readonly view of the
  // admin-shipped script with COPY + RESTORE buttons.
  const handleRestoreScript = useCallback(() => {
    const seed = activeAsset?.script ?? '';
    if (!seed) return;
    const lockedBlocks = blocks.filter((b) => b.locked);
    const msg = lockedBlocks.length > 0
      ? 'This will delete all unlocked blocks and restore the original script in one block. Locked blocks will be kept.'
      : 'This will delete all existing blocks and restore the original script in one block.';
    if (!window.confirm(msg)) return;
    if (pipelineMode !== 'idle') cancelPipeline();
    stopPlayback();
    const next: BlockT[] = [...lockedBlocks, makeEmptyBlock(seed, activeAssetVoiceId)];
    updateActiveBlocks(() => next);
    setActiveBlockIdx(next.length - 1);
    setNewBlockIdx(next.length - 1);
    bumpSkip();
  }, [activeAsset?.script, activeAssetVoiceId, blocks, pipelineMode, cancelPipeline, stopPlayback, updateActiveBlocks, bumpSkip, setActiveBlockIdx, setNewBlockIdx]);

  // Once-per-device "click the star" hint on the very first generated take
  // (GitHub #1 point 1). Any star click — or the pill's × — retires it.
  const [starHintSeen, setStarHintSeen] = useState(() => hasSeenStarHint());
  const dismissStarHint = () => {
    markStarHintSeen();
    setStarHintSeen(true);
  };
  const hintTakeId = useMemo(() => {
    if (starHintSeen) return undefined;
    if (blocks.some((b) => b.favIdx >= 0)) return undefined;
    const first = blocks.find((b) => b.takes.length > 0);
    return first?.takes[0]?.id;
  }, [starHintSeen, blocks]);

  const handleChipFav = (blockIdx: number, takeIdx: number) => {
    if (!starHintSeen) dismissStarHint();
    const b = blocks[blockIdx];
    if (!b) return;
    const nextFav = b.favIdx === takeIdx ? -1 : takeIdx;
    setBlockFavIdx(blockIdx, nextFav);
    // Auto-position the newly favorited take on the Master timeline. If the
    // take's metadata hasn't loaded yet (duration === 0), the audio.addEventListener
    // in handleGenerate/generateTakeForBlock will fire the auto-position
    // after duration is known. When a user manually picks an older take the
    // metadata is already populated — run immediately.
    if (nextFav >= 0) {
      const take = b.takes[nextFav];
      if (take && take.duration > 0) {
        updateActiveBlocks((prev) => autoPositionFavOnTimeline(blockIdx, prev, activeAsset?.voiceIds ?? []));
      }
    }
  };

  const handleChipDelete = (blockIdx: number, takeIdx: number) => {
    const target = blocks[blockIdx]?.takes[takeIdx];
    if (!target) return;
    const takeNum = parseInt(target.id.split('-')[1] ?? '?', 10);
    const label = isFinite(takeNum) ? `Take ${takeNum}` : 'this take';
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    // Stop playback if the deleted take is currently playing.
    if (playing && playing.blockIdx === blockIdx && playing.takeIdx === takeIdx) {
      stopPlayback();
    }
    // No blob: URL to revoke — the IDB audio entry goes with the next
    // autosave's orphan cleanup.
    removeTakeFromStore(blockIdx, takeIdx);
  };

  const handleAddBlock = () => {
    updateActiveBlocks((prev) => [...prev, makeEmptyBlock('', activeAssetVoiceId)]);
    setActiveBlockIdx(blocks.length);
    setNewBlockIdx(blocks.length);
    bumpSkip();
  };

  // ── Param panel mutation ─────────────────────────────────────────────
  const handleParamsChange = (next: TtsParams) => {
    const modelChanged = next.model !== activeBlock?.params.model;
    updateActiveBlocks((prev) => {
      if (!prev[activeBlockIdx]) return prev;
      const nextBlocks = prev.slice();
      nextBlocks[activeBlockIdx] = { ...prev[activeBlockIdx], params: next };
      return nextBlocks;
    });
    // Panel height changes on model swap → skip transition for one paint.
    if (modelChanged) bumpSkip();
  };

  // Multi-voice (#173) Phase 4: per-block voice picker writes here. Existing
  // takes keep their own voiceId — switching the block's voice only changes
  // what the NEXT generate uses (resolveBlockVoiceId reads block.voiceId at
  // generate-time).
  const handleBlockVoiceChange = (voiceId: string) => {
    updateActiveBlocks((prev) => {
      if (!prev[activeBlockIdx]) return prev;
      const nextBlocks = prev.slice();
      nextBlocks[activeBlockIdx] = { ...prev[activeBlockIdx], voiceId };
      return nextBlocks;
    });
  };

  return {
    handleTextChange,
    handleToggleLock,
    handleDuplicate,
    handleMergeUp,
    handleMergeDown,
    handleSplit,
    handleDelete,
    handleDownloadBlock,
    handleMoveUp,
    handleMoveDown,
    handleResetPositions,
    handleBlocksReordered,
    autoSplitFeedback,
    handleAutoSplitAll,
    handleRestoreScript,
    hintTakeId,
    dismissStarHint,
    handleChipFav,
    handleChipDelete,
    handleAddBlock,
    handleParamsChange,
    handleBlockVoiceChange,
  };
}
